import moment, { Moment } from "moment";
import { date as yupDate, DateSchema } from "yup";
import { DATE_TIME_FORMAT } from "@lhd/constants";

/**
 * Shared date-picker helpers.
 *
 * Typed input accepts yyyy/mm/dd (also yyyy/m/d and values already stored by
 * current pickers). Calendar selection is a moment. The field value is
 * 1999年2月12日. Use toDatePickerApiDate when an API still expects YYYY-MM-DD.
 *
 * Pass DATE_PICKER_PARSE_FORMATS to antd DatePicker `format`
 * (first entry is the display format) and save with formatDatePickerSaveValue.
 */

/** Saved and displayed form: 1999年2月12日 (no zero padding). */
export const DATE_PICKER_SAVE_FORMAT = DATE_TIME_FORMAT.DATE_JP_MD;

/**
 * First format is what the field shows. The rest are accepted while typing
 * or when reading a value already stored by an existing picker.
 */
export const DATE_PICKER_PARSE_FORMATS: string[] = [
  DATE_TIME_FORMAT.DATE_JP_MD,
  DATE_TIME_FORMAT.DATE_JP,
  "YYYY/M/D",
  DATE_TIME_FORMAT.DATE_DEFAULT,
  DATE_TIME_FORMAT.DATE_DASH,
  "YYYY-M-D",
  DATE_TIME_FORMAT.DATE_TIME_DASH_SEC,
  DATE_TIME_FORMAT.DATE_TIME,
];

const toHalfWidth = (value: string) =>
  value.replace(/[\uff01-\uff5e]/g, (ch) =>
    String.fromCharCode(ch.charCodeAt(0) - 0xfee0),
  );

const normalizeTypedDate = (value: string) =>
  toHalfWidth(value)
    .trim()
    // Drop weekday suffix used by some form fields: "2026/01/05 (月)"
    .replace(/\s*\([^)]*\)\s*$/, "")
    // Drop time used by CloneApp DATE fields: "2026-01-05 00:00:00"
    .replace(/\s+\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?$/, "")
    .trim();

/** yyyy/mm/dd, yyyy-mm-dd, or 1999年2月12日. Shorter text is still being typed. */
const COMPLETE_TYPED_DATE =
  /^(?:\d{4}\/\d{2}\/\d{2}|\d{4}-\d{2}-\d{2}|\d{4}年\d{1,2}月\d{1,2}日)$/;

export function isCompleteDatePickerText(value: string): boolean {
  return COMPLETE_TYPED_DATE.test(normalizeTypedDate(value));
}

const isExactDate = (
  parsed: Moment,
  year: number,
  month: number,
  day: number,
) =>
  parsed.isValid() &&
  parsed.year() === year &&
  parsed.month() + 1 === month &&
  parsed.date() === day;

/**
 * Parse a typed yyyy/mm/dd string, a calendar moment, or a value already
 * stored by an existing date field. Invalid or empty input returns null.
 */
export function parseDatePickerInput(
  value: Moment | string | Date | null | undefined,
): Moment | null {
  if (value == null || value === "") return null;

  if (moment.isMoment(value)) {
    return value.isValid() ? value.clone().startOf("day") : null;
  }

  if (value instanceof Date) {
    const parsed = moment(value);
    return parsed.isValid() ? parsed.startOf("day") : null;
  }

  const text = normalizeTypedDate(String(value));
  if (!text) return null;

  const parsed = moment(text, DATE_PICKER_PARSE_FORMATS, true);
  if (!parsed.isValid()) return null;

  return isExactDate(parsed, parsed.year(), parsed.month() + 1, parsed.date())
    ? parsed.startOf("day")
    : null;
}

/**
 * Value to store after typing or picking a date.
 * "1999/02/12" and a calendar moment both become "1999年2月12日".
 */
export function formatDatePickerSaveValue(
  value: Moment | string | Date | null | undefined,
): string | null {
  const parsed = parseDatePickerInput(value);
  if (!parsed) return null;
  return parsed.format(DATE_PICKER_SAVE_FORMAT);
}

/**
 * True when `end` is the same calendar day or later than `start`.
 * Accepts 1999年2月12日, yyyy/mm/dd, and YYYY-MM-DD. A missing side is not an error.
 */
export function isDatePickerOnOrAfter(
  end: Moment | string | Date | null | undefined,
  start: Moment | string | Date | null | undefined,
): boolean {
  const endApi = toDatePickerApiDate(end);
  const startApi = toDatePickerApiDate(start);
  if (!endApi || !startApi) return true;
  return endApi >= startApi;
}

/** YYYY-MM-DD for APIs and comparisons. Accepts the saved Japanese form and older picker values. */
export function toDatePickerApiDate(
  value: Moment | string | Date | null | undefined,
): string | null {
  const parsed = parseDatePickerInput(value);
  if (!parsed) return null;
  return parsed.format(DATE_TIME_FORMAT.DATE_DASH);
}

/** Formats accepted when typing into a date+time picker. */
export const DATE_TIME_PICKER_PARSE_FORMATS: string[] = [
  DATE_TIME_FORMAT.DATE_JP_TIME,
  DATE_TIME_FORMAT.DATE_JP_MD_TIME,
  DATE_TIME_FORMAT.DATE_TIME_JP,
  DATE_TIME_FORMAT.DATE_TIME,
  DATE_TIME_FORMAT.DATE_TIME_DASH_SEC,
  DATE_TIME_FORMAT.DATE_TIME_DASH,
  "YYYY-MM-DDTHH:mm",
  ...DATE_PICKER_PARSE_FORMATS,
];

const normalizeTypedDateTime = (value: string) =>
  toHalfWidth(value)
    .trim()
    // Drop weekday suffix: "2026/01/05 (月) 10:30" or "2026年01月05日 (月) 10:30"
    .replace(/\s*\([^)]*\)\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Parse typed date+time (yyyy/mm/dd HH:mm or 年月日 HH:mm) or a moment.
 * Date-only input becomes start of that day.
 */
export function parseDateTimePickerInput(
  value: Moment | string | Date | null | undefined,
): Moment | null {
  if (value == null || value === "") return null;

  if (moment.isMoment(value)) {
    return value.isValid() ? value.clone() : null;
  }

  if (value instanceof Date) {
    const parsed = moment(value);
    return parsed.isValid() ? parsed : null;
  }

  const text = normalizeTypedDateTime(String(value));
  if (!text) return null;

  const withTime = moment(text, DATE_TIME_PICKER_PARSE_FORMATS, true);
  if (withTime.isValid()) return withTime;

  return parseDatePickerInput(text);
}

/** Yup date field that accepts 1999年2月12日, yyyy/mm/dd, and existing picker strings. */
export function yupDatePicker(): DateSchema<Date | null | undefined> {
  return yupDate()
    .transform((value: unknown, originalValue: unknown) => {
      if (originalValue == null || originalValue === "") return null;
      if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
      const api = toDatePickerApiDate(
        originalValue as Moment | string | Date | null | undefined,
      );
      if (!api) return new Date(NaN);
      return new Date(`${api}T00:00:00`);
    })
    .nullable() as DateSchema<Date | null | undefined>;
}
